import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials ensures the httpOnly session_token cookie travels with requests.
const client = axios.create({
  baseURL: API,
  timeout: 60000,
  withCredentials: true,
});

// Auto-redirect on password-layer gates so any page stuck with a stale
// in-memory user gets bounced to the correct flow page.
client.interceptors.response.use(
  (r) => r,
  (error) => {
    const detail = error?.response?.data?.detail;
    const code = typeof detail === "object" ? detail?.code : null;
    const path = window.location.pathname;
    if (code === "PASSWORD_SET_REQUIRED" && path !== "/set-password") {
      window.location.assign("/set-password");
    } else if (code === "PASSWORD_UNLOCK_REQUIRED" && path !== "/unlock") {
      window.location.assign("/unlock");
    }
    return Promise.reject(error);
  }
);

export const authMe = () => client.get("/auth/me").then((r) => r.data);
export const authCallback = (sessionId) =>
  client.post("/auth/callback", { session_id: sessionId }).then((r) => r.data);
export const authLogout = () => client.post("/auth/logout").then((r) => r.data);
export const setPassword = (password) =>
  client.post("/auth/set-password", { password }).then((r) => r.data);
export const unlockPassword = (password) =>
  client.post("/auth/unlock", { password }).then((r) => r.data);
export const resetUserPassword = (userId, newPassword) =>
  client.post("/auth/reset-password", { user_id: userId, new_password: newPassword }).then((r) => r.data);

// Self-service password reset REQUEST (v18.99.9). A signed-in user
// who forgot their password files a request; the Architect approves
// it from their inbox and a temp password is generated.
export const requestPasswordReset = (reason) =>
  client.post("/auth/request-password-reset", { reason: reason || null }).then((r) => r.data);

// Architect-only — security audit rollup (v19.16)
export const getArchitectAudit = (limitRecent = 30, limitRecurring = 15) =>
  client
    .get(`/architect/audit?limit_recent=${limitRecent}&limit_recurring=${limitRecurring}`)
    .then((r) => r.data);

// Architect-only — password-reset inbox.
export const listPasswordResetRequests = (status = "pending") =>
  client.get(`/architect/password-reset-requests?status=${encodeURIComponent(status)}`).then((r) => r.data);

export const approvePasswordResetRequest = (requestId, customPassword) =>
  client
    .post("/architect/password-reset-requests/approve", {
      request_id: requestId,
      custom_password: customPassword || null,
    })
    .then((r) => r.data);

export const denyPasswordResetRequest = (requestId) =>
  client
    .post("/architect/password-reset-requests/deny", { request_id: requestId })
    .then((r) => r.data);

export const sendChat = (sessionId, message, attachments) =>
  client
    .post("/chat", {
      session_id: sessionId,
      message,
      attachments: attachments && attachments.length ? attachments : null,
    })
    .then((r) => r.data);

export const transcribeVoice = (blob) => {
  const form = new FormData();
  // Browsers emit webm/opus by default; OpenAI Whisper handles it.
  const filename = (blob.type && blob.type.includes("ogg")) ? "voice.ogg" : "voice.webm";
  form.append("audio", blob, filename);
  return client
    .post("/voice/transcribe", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 90000,
    })
    .then((r) => r.data);
};

export const verifyProvenance = (payload) =>
  client.post("/provenance/verify", payload).then((r) => r.data);

export const getProvenanceForSession = (sessionId) =>
  client.get(`/provenance/session/${sessionId}`).then((r) => r.data);

// Scholar's Citation Tether — Phase 2 Challenge Loop
export const challengeCitation = ({ idPrefix, reason }) =>
  client
    .post(`/citations/${idPrefix}/challenge`, { reason })
    .then((r) => r.data);

export const getCitationLineage = (citationId) =>
  client.get(`/citations/${citationId}/lineage`).then((r) => r.data);

export const triangulateCitation = (citationId, { force = false } = {}) =>
  client
    .post(`/citations/${citationId}/triangulate${force ? "?force=true" : ""}`)
    .then((r) => r.data);

// Truth Ledger · public auditor surface (no auth required)
export const getTruthLedger = ({ status, limit = 100 } = {}) =>
  client
    .get("/ledger/citations", { params: { status, limit } })
    .then((r) => r.data);

// Research Queue · public searchable fact database (v19.40.4)
// Defaults `include_resolved=false` (personal queue behaviour); the
// public /research page passes it explicitly so confirmed facts stay
// browsable.
export const getResearchQueue = ({ status, category, limit = 100, includeResolved = false } = {}) =>
  client
    .get("/research-queue", {
      params: { status, category, limit, include_resolved: includeResolved },
    })
    .then((r) => r.data);
export const recordResearcherVerdict = (citationId, verdict, note) =>
  client
    .post(`/research-queue/${citationId}/verdict`, { verdict, note: note || null })
    .then((r) => r.data);
export const getResearchCategories = () =>
  client.get("/research-queue/categories").then((r) => r.data);
export const setCitationCategory = (citationId, category) =>
  client.post(`/research-queue/${citationId}/category`, { category }).then((r) => r.data);

// Truth Blocks · Proof-of-Verification (PoVT) — public auditor surface
export const listTruthBlocks = ({ limit = 50, full = false } = {}) =>
  client
    .get("/truth-blocks", { params: { limit, full } })
    .then((r) => r.data);
export const getTruthBlock = (blockId) =>
  client.get(`/truth-blocks/${blockId}`).then((r) => r.data);
export const getTruthBlockProof = (blockId, citationId) =>
  client.get(`/truth-blocks/${blockId}/proof/${citationId}`).then((r) => r.data);
export const verifyTruthChain = () =>
  client.get("/truth-blocks/verify").then((r) => r.data);
export const getTruthBlocksPending = () =>
  client.get("/truth-blocks/pending").then((r) => r.data);
export const sealTruthBlock = () =>
  client.post("/truth-blocks/seal").then((r) => r.data);

// AEGIS coin · proof-of-cognitive-labor wallet
export const getAegisSupply = () =>
  client.get("/aegis/supply").then((r) => r.data);
export const getAegisPricing = () =>
  client.get("/aegis/pricing").then((r) => r.data);

// Membership tiers + Solana payment verification
export const getMembership = () =>
  client.get("/membership/me").then((r) => r.data);
export const verifySolanaPayment = ({ signature, sender_wallet, note }) =>
  client
    .post("/membership/verify-solana-tx", {
      signature,
      sender_wallet: sender_wallet || null,
      note: note || null,
    })
    .then((r) => r.data);
// v19.40 · $10 USDC-on-Solana stablecoin path (verified on-chain).
export const verifySolanaUsdcPayment = ({ signature, sender_wallet, note }) =>
  client
    .post("/membership/verify-solana-usdc-tx", {
      signature,
      sender_wallet: sender_wallet || null,
      note: note || null,
    })
    .then((r) => r.data);
// v19.40 · $10 PayPal path (queues for Architect manual review).
export const submitPayPalPayment = ({ transaction_id, payer_email, screenshot_url, note }) =>
  client
    .post("/membership/submit-paypal-payment", {
      transaction_id,
      payer_email: payer_email || null,
      screenshot_url: screenshot_url || null,
      note: note || null,
    })
    .then((r) => r.data);
// v19.40 · Architect · trigger the stuck-submission classifier watcher.
export const architectRunWatcherNow = () =>
  client.post("/architect/watcher/run-now").then((r) => r.data);
export const architectGetWatcherHistory = (limit = 30) =>
  client.get("/architect/watcher/history", { params: { limit } }).then((r) => r.data);
export const getMembershipPending = (limit = 50) =>
  client.get("/membership/pending", { params: { limit } }).then((r) => r.data);
export const approveMembershipPending = (payload) =>
  client.post("/membership/approve-pending", payload).then((r) => r.data);
export const getBuilderGrantsArchive = (limit = 100) =>
  client.get("/membership/grants-archive", { params: { limit } }).then((r) => r.data);
export const reviewBuilderGrant = (signature, note) =>
  client
    .post("/membership/grants-archive/review", { signature, note: note || null })
    .then((r) => r.data);

// User-submitted truths/lies — triangulated then architect-verified
export const createSubmission = ({ claim, claim_type, sources, note }) =>
  client
    .post("/submissions/create", {
      claim,
      claim_type,
      sources: sources || [],
      note: note || null,
    })
    .then((r) => r.data);
export const getMySubmissions = (limit = 50) =>
  client.get("/submissions/me", { params: { limit } }).then((r) => r.data);
export const getPendingSubmissions = (limit = 100, status) =>
  client
    .get("/submissions/pending", { params: { limit, status } })
    .then((r) => r.data);
export const getSubmission = (id) =>
  client.get(`/submissions/${id}`).then((r) => r.data);
export const verifySubmission = (id, verdict, note) =>
  client
    .post(`/submissions/${id}/verify`, { verdict, note: note || null })
    .then((r) => r.data);
export const regroundSubmission = (id) =>
  client.post(`/submissions/${id}/reground`).then((r) => r.data);
export const setAtomVerdict = (submissionId, atomId, verdict) =>
  client
    .post(`/submissions/${submissionId}/atoms/${atomId}/verdict`, { verdict })
    .then((r) => r.data);
export const getProofBounties = (limit = 50, status = "open") =>
  client
    .get("/proof-bounties", { params: { limit, status } })
    .then((r) => r.data);
export const submitBountyEvidence = (bountyId, payload) =>
  client
    .post(`/proof-bounties/${bountyId}/evidence`, payload)
    .then((r) => r.data);

// ── Wallet handles ─────────────────────────────────────────────
export const signupEmail = (payload) =>
  client.post("/auth/signup", payload).then((r) => r.data);
export const loginEmail = (payload) =>
  client.post("/auth/login", payload).then((r) => r.data);

// ── AEGIS economy ────────────────────────────────────────────────
export const getAegisLeaderboard = (limit = 50) =>
  client.get("/aegis/leaderboard", { params: { limit } }).then((r) => r.data);
export const getAegisBurns = (limit = 100) =>
  client.get("/aegis/burns", { params: { limit } }).then((r) => r.data);
export const verifyAegisBurns = () =>
  client.get("/aegis/burns/verify").then((r) => r.data);
export const postAegisMembershipPaid = (payload) =>
  client.post("/aegis/membership-paid", payload).then((r) => r.data);
export const getAegisWallet = (limit = 50, scope = "auto") =>
  client.get("/aegis/wallet", { params: { limit, scope } }).then((r) => r.data);
export const getAegisWalletsDirectory = (limit = 200) =>
  client.get("/aegis/wallets", { params: { limit } }).then((r) => r.data);
export const getAegisWalletDetail = (walletId, limit = 100) =>
  client
    .get(`/aegis/wallets/${encodeURIComponent(walletId)}`, { params: { limit } })
    .then((r) => r.data);
export const getAegisCoin = (coinId) =>
  client.get(`/aegis/coin/${coinId}`).then((r) => r.data);
export const verifyAegisChain = () =>
  client.get("/aegis/verify").then((r) => r.data);
export const transferAegis = ({ coin_ids, to_username, to_user_id, memo }) =>
  client
    .post("/aegis/transfer", {
      coin_ids,
      to_username: to_username || null,
      to_user_id: to_user_id || null,
      memo: memo || null,
    })
    .then((r) => r.data);
export const quickTransferAegis = ({ coin_count, to_username, to_user_id, memo }) =>
  client
    .post("/aegis/quick-transfer", {
      coin_count,
      to_username: to_username || null,
      to_user_id: to_user_id || null,
      memo: memo || null,
    })
    .then((r) => r.data);

// Public handles (usernames) — claim + lookup
export const claimUsername = (username) =>
  client.post("/users/me/username", { username }).then((r) => r.data);
export const lookupUsername = (username) =>
  client
    .get(`/users/lookup/${encodeURIComponent(username.replace(/^@/, ""))}`)
    .then((r) => r.data);
export const getAegisTransfers = (limit = 50) =>
  client.get("/aegis/transfers", { params: { limit } }).then((r) => r.data);
export const verifyAegisTransfers = () =>
  client.get("/aegis/transfers/verify").then((r) => r.data);

// Truth Compiler · pre-LLM retrieval of cryptographically verified facts
export const getTruthCompilerStats = () =>
  client.get("/truth-compiler/stats").then((r) => r.data);
export const truthCompilerRecall = (q, k = 5) =>
  client.get("/truth-compiler/recall", { params: { q, k } }).then((r) => r.data);
export const truthCompilerBackfill = () =>
  client.post("/truth-compiler/backfill").then((r) => r.data);
export const truthCompilerIndex = (limit = 200) =>
  client.get("/truth-compiler/index", { params: { limit } }).then((r) => r.data);

// Smart Contract · Sentinel-grade escrow ledger
export const scGetAegisToken = () =>
  client.get("/smart-contract/aegis-token").then((r) => r.data);
export const scGetStateMachine = () =>
  client.get("/smart-contract/state-machine").then((r) => r.data);
export const scGetReserve = () =>
  client.get("/smart-contract/reserve").then((r) => r.data);
export const scAttestReserve = (payload) =>
  client.post("/smart-contract/reserve", payload).then((r) => r.data);
export const scListContracts = () =>
  client.get("/smart-contract/contracts").then((r) => r.data);
export const scCreateContract = (payload) =>
  client.post("/smart-contract/contracts", payload).then((r) => r.data);
export const scGetContract = (id) =>
  client.get(`/smart-contract/contracts/${id}`).then((r) => r.data);
export const scAdvanceContract = (id, payload) =>
  client.post(`/smart-contract/contracts/${id}/advance`, payload).then((r) => r.data);

// Layer 10 · Web grounding (Gemini Google Search)
export const groundCitation = (citationId, { force = false } = {}) =>
  client
    .post(`/citations/${citationId}/ground${force ? "?force=true" : ""}`)
    .then((r) => r.data);

// Governance metrics — public auditor surface, used by the live HUD marquee
export const getGovernanceMetrics = () =>
  client.get("/governance/metrics").then((r) => r.data);

export const getChatContext = (sessionId) =>
  client.get("/chat/context", { params: { session_id: sessionId } }).then((r) => r.data);

// Session history rehydrate
export const getSessionLogs = (sessionId, limit = 200) =>
  client.get("/logs", { params: { session_id: sessionId, limit } }).then((r) => r.data);

// Archive of Remembrance · surgical Forget (Architect-only)
export const listMemories = () =>
  client.get("/memories").then((r) => r.data);
export const forgetMemory = (memoryId) =>
  client.delete(`/memories/${encodeURIComponent(memoryId)}`).then((r) => r.data);
export const forgetPulse = (pulseNumber) =>
  client.delete(`/memories/pulse/${pulseNumber}`).then((r) => r.data);

export const getSessionMessages = (sessionId) =>
  client.get(`/sessions/${sessionId}/messages`).then((r) => r.data);

export const getSessionStats = (sessionId) =>
  client.get(`/sessions/${sessionId}/stats`).then((r) => r.data);

export const getLogs = (limit = 100, skip = 0) =>
  client.get(`/logs`, { params: { limit, skip } }).then((r) => r.data);

export const getSentinelStatus = () =>
  client.get("/sentinel/status").then((r) => r.data);

export const getSentinelConfig = () =>
  client.get("/sentinel/config").then((r) => r.data);

export const updateSentinelConfig = (cfg, { confirmCredentialChange } = {}) =>
  client
    .put("/sentinel/config", cfg, {
      headers: confirmCredentialChange
        ? { "X-Confirm-Credential-Change": confirmCredentialChange }
        : undefined,
    })
    .then((r) => r.data);

export const listApiKeys = () => client.get("/keys").then((r) => r.data);
export const createApiKey = (name) =>
  client.post("/keys", { name }).then((r) => r.data);
export const revokeApiKey = (keyId) =>
  client.delete(`/keys/${keyId}`).then((r) => r.data);

// Layer 7 · Architect's Handshake
export const layer7Audit = (sentinelId, accessCode) =>
  client
    .post("/layer7/audit", { sentinel_id: sentinelId, access_code: accessCode })
    .then((r) => r.data);

// Layer 0 · Aegis
export const aegisStatus = () => client.get("/aegis/status").then((r) => r.data);
export const aegisRelease = (accessCode) =>
  client.post("/aegis/release", { access_code: accessCode }).then((r) => r.data);

// 7-Layer Security Enclosure
export const layersStatus = () => client.get("/layers/status").then((r) => r.data);

// Bio-Digital
export const ingestTelemetry = (payload) =>
  client.post("/environment/telemetry", payload).then((r) => r.data);
export const readTelemetry = () =>
  client.get("/environment/telemetry").then((r) => r.data);
export const handshakeExport = () =>
  client.get("/handshake/export").then((r) => r.data);
export const handshakeBroadcast = (payload) =>
  client.post("/handshake/broadcast", payload).then((r) => r.data);
export const listReceipts = (limit = 50) =>
  client.get("/receipts", { params: { limit } }).then((r) => r.data);

// Black Box · Architect-gated global sensitive-data vault
export const blackBoxStatus = () =>
  client.get("/blackbox/status").then((r) => r.data);
export const blackBoxUnlock = (accessCode) =>
  client.post("/blackbox/unlock", { access_code: accessCode }).then((r) => r.data);

export const safetyTelemetry = () =>
  client.get("/safety/telemetry").then((r) => r.data);

// Architect's Archive (RAG documents)
export const listDocuments = () => client.get("/documents").then((r) => r.data);
export const createDocument = (name, content) =>
  client.post("/documents", { name, content }).then((r) => r.data);
export const uploadDocumentFile = (file) => {
  const form = new FormData();
  form.append("file", file);
  return client
    .post("/documents/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const deleteDocument = (docId) =>
  client.delete(`/documents/${docId}`).then((r) => r.data);
export const getDocument = (docId) =>
  client.get(`/documents/${docId}`).then((r) => r.data);


// Site metadata · Architect donation URL etc. (public)
export const siteMeta = () =>
  client.get("/site/meta").then((r) => r.data);

// ─── Ask the Architect · Forum ──────────────────────────────────────
export const forumList = (limit = 50, skip = 0) =>
  client.get("/forum/questions", { params: { limit, skip } }).then((r) => r.data);

export const forumPost = (body) =>
  client.post("/forum/questions", { body }).then((r) => r.data);

export const forumAnswer = (qid, body) =>
  client.post(`/forum/questions/${qid}/answer`, { body }).then((r) => r.data);

export const forumDismiss = (qid) =>
  client.post(`/forum/questions/${qid}/dismiss`).then((r) => r.data);

export const forumUnreadCount = () =>
  client.get("/forum/unread_count").then((r) => r.data);

// Orion Visualizer
export const orionVisualize = (prompt, sessionId, originalMessage, referenceImages) =>
  client
    .post("/orion/visualize", {
      prompt,
      session_id: sessionId,
      original_message: originalMessage || null,
      reference_images:
        referenceImages && referenceImages.length ? referenceImages : null,
    })
    .then((r) => r.data);
export const orionGallery = () => client.get("/orion/gallery").then((r) => r.data);
export const orionImageUrl = (visId) => `${API}/orion/image/${visId}`;
export const orionIntent = (message, sessionId) =>
  client
    .post("/orion/intent", { message, session_id: sessionId })
    .then((r) => r.data);

export const getSky = () => client.get("/sky").then((r) => r.data);
export const walkSky = (origin) =>
  client.post("/sky/walk", { origin }).then((r) => r.data);

// Aegis Solana SPL bridge — withdraw AEGIS to an external wallet.
export const getAegisSplConfig = () => client.get("/aegis/spl/config").then((r) => r.data);

// Multi-language · translate any user-submitted text to English (or another target).
// Backed by /api/translate → Gemini 2.5 Flash. Cached server-side.
export const translateText = (text, target_lang = "en") =>
  client.post("/translate", { text, target_lang }).then((r) => r.data);

// Projective Geometry Rendering Layer (v19.2) — Architect-only diagnostic studio.
export const pgrlGetState = () =>
  client.get("/pgrl/state").then((r) => r.data);
export const pgrlNeverHarmLog = (limit = 100) =>
  client.get(`/pgrl/never-harm-log?limit=${limit}`).then((r) => r.data);
export const pgrlVerifyChain = () =>
  client.post("/pgrl/never-harm/verify").then((r) => r.data);
export const pgrlPropose = (directive, focus) =>
  client
    .post("/pgrl/propose", { directive, focus: focus || null })
    .then((r) => r.data);
// Architect-Override Commit — persists proposed edges into
// neuro_symbolic_facts with a signed provenance receipt.
export const pgrlCommit = (directive, edges) =>
  client.post("/pgrl/commit", { directive, edges }).then((r) => r.data);
// Node Expansion — the pop-out 3D deep-view of a single vertex.
export const pgrlExpandNode = (label, max_facets = 32) =>
  client.post("/pgrl/expand-node", { label, max_facets }).then((r) => r.data);
// Node image (Nano Banana) — first call may return {status:"queued"}; poll
// again in a few seconds until {status:"ready", data_url:"data:image/..."}.
export const pgrlNodeImage = (label) =>
  client
    .get(`/pgrl/node-image?label=${encodeURIComponent(label)}`)
    .then((r) => r.data);
export const pgrlRegenNodeImage = (label) =>
  client.post("/pgrl/node-image/regenerate", { label }).then((r) => r.data);
// Backfill — one shot: queue Nano Banana for every missing node image.
export const pgrlBackfillImages = () =>
  client.post("/pgrl/node-image/backfill").then((r) => r.data);
// v19.40.13 · Gallery listing — every cached node image, newest first.
// Backend supports ?q= substring filter, but the current UI filters client-
// side over the loaded page (200 rows fits comfortably in memory).
export const pgrlListNodeImages = ({ limit = 200, q } = {}) =>
  client
    .get("/pgrl/node-images", { params: { limit, q } })
    .then((r) => r.data);
// v19.40.13 · Timeline · every distinct label in the neuro-symbolic library,
// sorted by first-seen timestamp descending. Powers the "Library" view that
// replaces the crowded 3D scene when the user just wants to find one node.
export const pgrlNodesTimeline = ({ limit = 500, q } = {}) =>
  client
    .get("/pgrl/nodes-timeline", { params: { limit, q } })
    .then((r) => r.data);
// v20.12 · Chronological chain of sealed citations with doubly-linked
// prev_id/next_id pointers so the UI (and any AI reader) can walk the
// ledger without jumping across the DB.
export const getTimeline = ({ limit = 500, era, q } = {}) =>
  client
    .get("/pgrl/timeline", { params: { limit, era, q } })
    .then((r) => r.data);
export const setCitationTemporalAnchor = (citationId, payload) =>
  client
    .post(`/architect/citations/${citationId}/temporal-anchor`, payload)
    .then((r) => r.data);
// Inconsistency resolver — Gemini proposes 2-3 concrete resolutions.
export const pgrlResolveInconsistency = (center_label, conflict) =>
  client
    .post("/pgrl/resolve-inconsistency", { center_label, conflict })
    .then((r) => r.data);
export const pgrlProbe = ({ kind, target, target2 }) => {
  const params = new URLSearchParams({ kind, target });
  if (target2) params.set("target2", target2);
  return client.get(`/pgrl/probe?${params}`).then((r) => r.data);
};
// WebSocket URL — the frontend opens this with document.cookie carrying
// the session_token, so the backend can authenticate without a header.
export const pgrlStreamUrl = () => {
  const base = API.replace(/^http/, "ws");
  return `${base}/pgrl/stream`;
};

// Obsidian Sink · Neuro-Symbolic Library (v19.0)
export const obsidianDeepDive = (topic, kSubclaims = 6) =>
  client
    .post("/obsidian/deep-dive", { topic, k_subclaims: kSubclaims })
    .then((r) => r.data);

export const listObsidianDeepDives = (limit = 20) =>
  client.get(`/obsidian/deep-dives?limit=${limit}`).then((r) => r.data);

export const getObsidianDeepDive = (reportId) =>
  client.get(`/obsidian/deep-dive/${encodeURIComponent(reportId)}`).then((r) => r.data);

export const getKnowledgeGraphTriples = ({ subject, predicate, limit = 50 } = {}) => {
  const params = new URLSearchParams();
  if (subject) params.append("subject", subject);
  if (predicate) params.append("predicate", predicate);
  params.append("limit", String(limit));
  return client.get(`/knowledge-graph/triples?${params}`).then((r) => r.data);
};

export const getKnowledgeGraphStats = () =>
  client.get("/knowledge-graph/stats").then((r) => r.data);

// Symbolic Inference Queue (v19.1) — compositional layer.
export const generateInferences = () =>
  client.post("/inference-queue/generate").then((r) => r.data);
export const listInferences = (status = "pending", limit = 50) =>
  client.get(`/inference-queue?status=${status}&limit=${limit}`).then((r) => r.data);
export const voteInference = (inferenceId, vote) =>
  client
    .post(`/inference-queue/${encodeURIComponent(inferenceId)}/vote`, { vote })
    .then((r) => r.data);
export const getAegisSplWallet = () => client.get("/aegis/spl/wallet").then((r) => r.data);
export const getAegisSplAuthorityBalance = () =>
  client.get("/aegis/spl/authority-balance").then((r) => r.data);
export const linkSolanaWallet = (solana_wallet) =>
  client.post("/aegis/spl/link-wallet", { solana_wallet }).then((r) => r.data);
export const withdrawAegisToSolana = (amount, to_wallet) =>
  client.post("/aegis/spl/withdraw", { amount, to_wallet }).then((r) => r.data);
export const getAegisSplWithdrawals = (limit = 50) =>
  client.get(`/aegis/spl/withdrawals?limit=${limit}`).then((r) => r.data);

// Architect-only direct grant — bypass payment, comp a user 30 days Builder.
// Accepts {username, email, user_id, note} — server tries them in that order.
export const grantBuilderDirect = (payload) =>
  client
    .post("/membership/grant-builder-direct", payload)
    .then((r) => r.data);

// Architect-only user search — typeahead for the Direct Grant picker.
export const architectSearchUsers = (q, limit = 20) =>
  client
    .get(`/architect/users/search?q=${encodeURIComponent(q)}&limit=${limit}`)
    .then((r) => r.data);

// Humanitarian Reserve (v18.99) — public.
export const getReserveStats = () => client.get("/reserve/stats").then((r) => r.data);
export const getReserveWallets = (page = 1, limit = 100, q = "") => {
  const params = new URLSearchParams({ page, limit });
  if (q) params.set("q", q);
  return client.get(`/reserve/wallets?${params}`).then((r) => r.data);
};
export const getBurnLedger = (limit = 100) =>
  client.get(`/reserve/burn-ledger?limit=${limit}`).then((r) => r.data);
export const triggerEmergencySignal = (password) =>
  client.post("/reserve/emergency-signal", { password }).then((r) => r.data);

// Profile — set a custom display name that overrides Google's profile name.
export const setDisplayName = (display_name) =>
  client.post("/auth/me/display-name", { display_name }).then((r) => r.data);

// Public truth feed — 3 buckets: fact, fiction, bounty.
export const getTruthFeed = (bucket = "fact", limit = 50, cursor = null) => {
  const params = new URLSearchParams({ bucket, limit });
  if (cursor) params.set("cursor", cursor);
  return client.get(`/feed?${params}`).then((r) => r.data);
};

export const disputeSubmission = (submission_id, reason) =>
  client.post(`/submissions/${submission_id}/dispute`, { reason }).then((r) => r.data);

// Architect-only — backfill the stuck submission queue with v18.99 rules.
export const architectBackfillQueue = () =>
  client.post("/architect/backfill-queue").then((r) => r.data);

// Architect-only — list opinion/bounty submissions that haven't minted yet
// so we can review + manually reclassify each as truth or lie.
export const architectListOpinions = (limit = 100) =>
  client
    .get(`/architect/submissions/opinions`, { params: { limit } })
    .then((r) => r.data);

// Architect-only — reclassify an opinion/bounty submission as verified
// truth or verified lie. Mints AEGIS to the original submitter.
export const architectReclassifySubmission = (submissionId, newType) =>
  client
    .post(`/architect/submissions/${submissionId}/reclassify`, {
      new_type: newType,
    })
    .then((r) => r.data);

// Architect-only — confirm a submission really is unclassifiable (not a
// factual claim). Removes it from the reclassify queue with no mint.
export const architectDismissUnclassifiable = (submissionId) =>
  client
    .post(`/architect/submissions/${submissionId}/dismiss-unclassifiable`)
    .then((r) => r.data);

// Architect-only — walk unminted opinion/bounty submissions and
// auto-mint any where Claude+Gemini agreed on a concrete true/false.
export const architectAutoClassifyAgreed = (limit = 200) =>
  client
    .post(`/architect/submissions/auto-classify-agreed`, null, {
      params: { limit },
    })
    .then((r) => r.data);

// Architect-only — find submissions that were closed as opinion because
// the LLMs errored out (budget exhausted, rate-limited, etc.), reset
// them to pending, and re-fire the triangulation pipeline.
export const architectRetryFailedTriangulations = (limit = 100) =>
  client
    .post(`/architect/submissions/retry-failed-triangulations`, null, {
      params: { limit },
    })
    .then((r) => r.data);

// Architect-only — scan recent Console messages from free users, find
// the buried factual claims, and (optionally) push them through the
// standard triangulation + mint pipeline so the original sender gets
// credited AEGIS. `commit=false` is a dry-run.
export const architectTriangulateConsoleHistory = ({
  hours = 6,
  commit = false,
  limit = 50,
} = {}) =>
  client
    .post("/architect/triangulate-console-history", { hours, commit, limit })
    .then((r) => r.data);

// Architect-only — find every console-reconcile submission stuck on
// status='triangulating' (because the background task crashed before
// writing back) and rerun the triangulation pipeline. One-shot rescue
// for rows created before the v18.99.1 bug fix.
export const architectRetriggerStuckReconciles = () =>
  client.post("/architect/retrigger-stuck-reconciles").then((r) => r.data);

// Architect-only — bulk-resolve every "uncited" citation on /research
// using the single-AI Quick Verdict lane. Decisive TRUE → mints AEGIS,
// decisive FALSE → denied (purged from Truth Compiler), uncertain → left
// for human review.
export const architectResolveUncitedCitations = ({
  limit = 50,
  minConfidence = 0.7,
  includeSingleSource = false,
} = {}) =>
  client
    .post("/architect/resolve-uncited-citations", {
      limit,
      min_confidence: minConfidence,
      include_single_source: includeSingleSource,
    })
    .then((r) => r.data);

// Architect-only — FULL autonomous research-queue drain (v18.99.8).
// Walks every pending citation (any epistemic_status), runs Quick
// Verdict, classifies as fact/fiction/bounty/opinion, mints AEGIS,
// rebalances past pool→submitter payouts. One button, one pass.
export const architectAutoProcessResearchQueue = ({
  limit = 200,
  minConfidence = 0.7,
  includeAlreadyGrounded = true,
  rebalancePoolPayouts = true,
} = {}) =>
  client
    .post("/architect/auto-process-research-queue", {
      limit,
      min_confidence: minConfidence,
      include_already_grounded: includeAlreadyGrounded,
      rebalance_pool_payouts: rebalancePoolPayouts,
    })
    .then((r) => r.data);

// Architect-only — re-sign coins whose prev_coin_hash links broke
// (typically from a concurrent-mint race before the lock was added).
// Original mint payload preserved exactly; only the link, signature,
// and coin_hash get recomputed under the architect's HMAC secret.
export const architectRepairAegisChain = () =>
  client.post("/architect/repair-aegis-chain").then((r) => r.data);

// Architect-only — full system check: walk every profile, find
// submissions/citations that are saved but unpaid, route each to the
// right mint path, return per-user payout breakdown.
export const architectSystemPayoutAudit = () =>
  client.post("/architect/system-payout-audit").then((r) => r.data);

// Architect-only — full ledger of every signed-in user (non-anon).
// Optional ?q= substring filter over handle/email/display/user_id.
export const architectUsersLedger = ({ q = "", limit = 500 } = {}) =>
  client
    .get(`/architect/users/ledger?q=${encodeURIComponent(q)}&limit=${limit}`)
    .then((r) => r.data);

// Architect-only — backfill the welcome mint for every signed-in user
// who never got one (pre-v18.99.5 accounts). Idempotent.
export const architectBackfillWelcomeMints = () =>
  client.post("/architect/backfill-welcome-mints").then((r) => r.data);

// Architect-only — for every existing citation born from chat, set
// submitter_user_id + user_submitted=true so the 35% verifier slice
// flows to the original asker on the next mint. Retro-fix for
// citations created before v18.99.6.
export const architectBackfillCitationSubmitters = () =>
  client.post("/architect/backfill-citation-submitters").then((r) => r.data);
